/**
 * PinataService - IPFS media storage via Pinata for Znoospace
 * 
 * Handles image and video uploads to IPFS via Pinata's SDK.
 * Includes per-wallet storage quota tracking with SOL-based upgrade payments.
 * 
 * Free tier: 50MB per wallet (images + video)
 * Paid tiers: Purchase additional storage via SOL micropayments
 * Video clips: Max 140 seconds (like X/Twitter)
 * 
 * Storage tracking is persisted in localStorage per wallet address.
 */

import { PinataSDK } from 'pinata';
import { secureStorage } from '../utils/security';

// ── Configuration ──
const PINATA_JWT = process.env.REACT_APP_PINATA_JWT || '';
const PINATA_GATEWAY = process.env.REACT_APP_PINATA_GATEWAY || '';

// Storage tiers (in bytes)
const STORAGE_TIERS = {
  free: {
    name: 'Free',
    limit: 50 * 1024 * 1024, // 50MB
    costSol: 0,
    label: '50 MB',
  },
  basic: {
    name: 'Basic',
    limit: 250 * 1024 * 1024, // 250MB
    costSol: 0.05, // 0.05 SOL (~$7)
    label: '250 MB',
  },
  pro: {
    name: 'Pro',
    limit: 1024 * 1024 * 1024, // 1GB
    costSol: 0.15, // 0.15 SOL (~$21)
    label: '1 GB',
  },
  unlimited: {
    name: 'Unlimited',
    limit: 10 * 1024 * 1024 * 1024, // 10GB (practical limit)
    costSol: 0.5, // 0.5 SOL (~$70)
    label: '10 GB',
  },
};

// File constraints
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
const MAX_VIDEO_DURATION = 140; // seconds (2:20, like X free tier)
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

class PinataService {
  constructor() {
    this.pinata = null;
    this.gateway = PINATA_GATEWAY;
    this.isReady = false;
    this._init();
  }

  _init() {
    if (PINATA_JWT) {
      try {
        this.pinata = new PinataSDK({ pinataJwt: PINATA_JWT, pinataGateway: this.gateway });
        this.isReady = true;
        console.log('✅ PinataService initialized');
      } catch (e) {
        console.warn('⚠️ PinataService init failed:', e.message);
      }
    } else {
      console.warn('⚠️ No REACT_APP_PINATA_JWT set — media uploads disabled');
    }
  }

  /** Check if Pinata is configured */
  isAvailable() {
    return this.isReady && !!this.pinata;
  }

  // ─────────────────────────────────────────────
  // Storage Quota Management
  // ─────────────────────────────────────────────

  /** Get storage key for a wallet */
  _storageKey(walletAddress) {
    return `znoospace_media_quota_${walletAddress}`;
  }

  /** Get current storage usage and tier for a wallet */
  getStorageInfo(walletAddress) {
    try {
      const raw = secureStorage.getItem(this._storageKey(walletAddress));
      if (raw) {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        return {
          used: data.used || 0,
          tier: data.tier || 'free',
          limit: STORAGE_TIERS[data.tier || 'free'].limit,
          tierName: STORAGE_TIERS[data.tier || 'free'].name,
          tierLabel: STORAGE_TIERS[data.tier || 'free'].label,
          uploads: data.uploads || [],
          purchaseHistory: data.purchaseHistory || [],
        };
      }
    } catch {}
    return {
      used: 0,
      tier: 'free',
      limit: STORAGE_TIERS.free.limit,
      tierName: 'Free',
      tierLabel: '50 MB',
      uploads: [],
      purchaseHistory: [],
    };
  }

  /** Check if wallet has enough storage for a file */
  hasStorageFor(walletAddress, fileSize) {
    const info = this.getStorageInfo(walletAddress);
    return info.used + fileSize <= info.limit;
  }

  /** Get remaining storage in bytes */
  getRemainingStorage(walletAddress) {
    const info = this.getStorageInfo(walletAddress);
    return Math.max(0, info.limit - info.used);
  }

  /** Get available upgrade tiers for a wallet */
  getUpgradeTiers(walletAddress) {
    const info = this.getStorageInfo(walletAddress);
    const currentTierIndex = Object.keys(STORAGE_TIERS).indexOf(info.tier);
    return Object.entries(STORAGE_TIERS)
      .filter(([, tier]) => tier.limit > info.limit)
      .map(([key, tier]) => ({
        key,
        ...tier,
        isCurrent: key === info.tier,
      }));
  }

  /** Record a storage upgrade purchase */
  recordUpgrade(walletAddress, tierKey, txSignature) {
    const info = this.getStorageInfo(walletAddress);
    const newTier = STORAGE_TIERS[tierKey];
    if (!newTier) return false;

    const data = {
      used: info.used,
      tier: tierKey,
      uploads: info.uploads,
      purchaseHistory: [
        ...info.purchaseHistory,
        {
          from: info.tier,
          to: tierKey,
          costSol: newTier.costSol,
          txSignature,
          timestamp: Date.now(),
        },
      ],
    };
    secureStorage.setItem(this._storageKey(walletAddress), JSON.stringify(data));
    console.log(`✅ Storage upgraded to ${newTier.name} for ${walletAddress.slice(0, 8)}`);
    return true;
  }

  /** Record a file upload in storage tracking */
  _recordUpload(walletAddress, fileSize, cid, fileName, mediaType) {
    const info = this.getStorageInfo(walletAddress);
    const data = {
      used: info.used + fileSize,
      tier: info.tier,
      uploads: [
        ...info.uploads,
        { cid, fileName, fileSize, mediaType, timestamp: Date.now() },
      ],
      purchaseHistory: info.purchaseHistory,
    };
    secureStorage.setItem(this._storageKey(walletAddress), JSON.stringify(data));
  }

  // ─────────────────────────────────────────────
  // File Validation
  // ─────────────────────────────────────────────

  /** Validate an image file */
  validateImage(file) {
    if (!file) return { valid: false, error: 'No file provided' };
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return { valid: false, error: `Invalid image type. Allowed: JPEG, PNG, GIF, WebP` };
    }
    if (file.size > MAX_IMAGE_SIZE) {
      return { valid: false, error: `Image too large. Max ${MAX_IMAGE_SIZE / 1024 / 1024}MB` };
    }
    return { valid: true };
  }

  /** Validate a video file (size only — duration checked separately) */
  validateVideo(file) {
    if (!file) return { valid: false, error: 'No file provided' };
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      return { valid: false, error: `Invalid video type. Allowed: MP4, WebM, MOV` };
    }
    if (file.size > MAX_VIDEO_SIZE) {
      return { valid: false, error: `Video too large. Max ${MAX_VIDEO_SIZE / 1024 / 1024}MB` };
    }
    return { valid: true };
  }

  /** Validate any media file */
  validateMedia(file) {
    if (!file) return { valid: false, error: 'No file provided' };
    if (file.type.startsWith('video/')) return this.validateVideo(file);
    return this.validateImage(file);
  }

  /**
   * Check video duration in the browser.
   * Returns a promise that resolves with { duration, valid, error? }
   */
  checkVideoDuration(file) {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const duration = video.duration;
        if (duration > MAX_VIDEO_DURATION) {
          resolve({
            duration,
            valid: false,
            error: `Video is ${Math.ceil(duration)}s — max allowed is ${MAX_VIDEO_DURATION}s (2:20). Please trim your video.`,
          });
        } else {
          resolve({ duration, valid: true });
        }
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        resolve({ duration: 0, valid: false, error: 'Could not read video metadata' });
      };
      video.src = URL.createObjectURL(file);
    });
  }

  // ─────────────────────────────────────────────
  // Upload
  // ─────────────────────────────────────────────

  /**
   * Upload a media file to Pinata IPFS.
   * 
   * @param {File} file - The file to upload
   * @param {string} walletAddress - Uploader's wallet address (for quota tracking)
   * @param {object} metadata - Optional metadata tags
   * @returns {Promise<object>} { success, cid, url, fileName, fileSize, mediaType }
   */
  async uploadMedia(file, walletAddress, metadata = {}) {
    if (!this.isAvailable()) {
      return { success: false, error: 'Pinata not configured. Set REACT_APP_PINATA_JWT.' };
    }

    // Validate file
    const validation = this.validateMedia(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Check video duration
    if (file.type.startsWith('video/')) {
      const durCheck = await this.checkVideoDuration(file);
      if (!durCheck.valid) {
        return { success: false, error: durCheck.error };
      }
    }

    // Check storage quota
    if (!this.hasStorageFor(walletAddress, file.size)) {
      const info = this.getStorageInfo(walletAddress);
      const remaining = this.getRemainingStorage(walletAddress);
      return {
        success: false,
        error: 'STORAGE_EXCEEDED',
        message: `Storage limit reached (${info.tierLabel} plan). ${this._formatBytes(remaining)} remaining. Upgrade your plan to upload more.`,
        storageInfo: info,
        upgradeTiers: this.getUpgradeTiers(walletAddress),
      };
    }

    try {
      console.log(`📤 Uploading ${file.name} to Pinata IPFS...`);

      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';

      // Direct fetch to Pinata API — avoids SDK wrapper that Brave Shields blocks
      const formData = new FormData();
      formData.append('file', file);

      const pinataMetadata = JSON.stringify({
        name: file.name,
        keyvalues: {
          app: 'znoospace',
          wallet: walletAddress,
          mediaType,
          ...(metadata || {}),
        },
      });
      formData.append('pinataMetadata', pinataMetadata);

      const pinataOptions = JSON.stringify({ cidVersion: 1 });
      formData.append('pinataOptions', pinataOptions);

      const res = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${PINATA_JWT}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const errBody = await res.text();
        throw new Error(`Pinata API ${res.status}: ${errBody}`);
      }

      const data = await res.json();
      const cid = data.IpfsHash;
      const url = this.gateway
        ? `https://${this.gateway}/ipfs/${cid}`
        : `https://gateway.pinata.cloud/ipfs/${cid}`;

      // Record upload in storage tracking
      this._recordUpload(walletAddress, file.size, cid, file.name, mediaType);

      console.log(`✅ Uploaded to IPFS: ${cid}`);

      return {
        success: true,
        cid,
        url,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        mediaType,
      };
    } catch (error) {
      console.error('❌ Pinata upload failed:', error);
      return { success: false, error: error.message || 'Upload failed' };
    }
  }

  // ─────────────────────────────────────────────
  // Storage Upgrade (SOL payment)
  // ─────────────────────────────────────────────

  /**
   * Create a SOL transfer transaction for storage upgrade.
   * The caller must sign and send this transaction.
   * 
   * @param {string} tierKey - Target tier key ('basic', 'pro', 'unlimited')
   * @param {object} walletAdapter - Wallet adapter with publicKey and signTransaction
   * @param {Connection} connection - Solana connection
   * @returns {Promise<object>} { success, signature, tier }
   */
  async purchaseStorageUpgrade(tierKey, walletAdapter, connection) {
    const tier = STORAGE_TIERS[tierKey];
    if (!tier) {
      return { success: false, error: `Unknown tier: ${tierKey}` };
    }
    if (tier.costSol === 0) {
      return { success: false, error: 'Free tier does not require payment' };
    }

    try {
      const { Transaction, SystemProgram, PublicKey, LAMPORTS_PER_SOL } = await import('@solana/web3.js');

      // Payment goes to the platform treasury wallet
      const TREASURY_WALLET = new PublicKey(
        process.env.REACT_APP_TREASURY_WALLET || 'DRtEKddBRvyNkRCnsMRz5C2JUx2CwVcVSFJicRVBsh3E'
      );

      const lamports = Math.ceil(tier.costSol * LAMPORTS_PER_SOL);

      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: walletAdapter.publicKey,
          toPubkey: TREASURY_WALLET,
          lamports,
        })
      );

      // Add a memo so the payment is traceable
      const { TransactionInstruction } = await import('@solana/web3.js');
      const memoInstruction = new TransactionInstruction({
        keys: [],
        programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
        data: Buffer.from(
          `Znoospace Storage Upgrade: ${JSON.stringify({ tier: tierKey, wallet: walletAdapter.publicKey.toString() })}`,
          'utf8'
        ),
      });
      transaction.add(memoInstruction);

      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = walletAdapter.publicKey;

      const signedTx = await walletAdapter.signTransaction(transaction);
      const signature = await connection.sendRawTransaction(signedTx.serialize());
      await connection.confirmTransaction(signature, 'confirmed');

      // Record the upgrade
      this.recordUpgrade(walletAdapter.publicKey.toString(), tierKey, signature);

      console.log(`✅ Storage upgraded to ${tier.name} — tx: ${signature}`);

      return {
        success: true,
        signature,
        tier: tierKey,
        tierName: tier.name,
        newLimit: tier.label,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`,
      };
    } catch (error) {
      console.error('❌ Storage upgrade payment failed:', error);
      return { success: false, error: error.message };
    }
  }

  // ─────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────

  _formatBytes(bytes) {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /** Get the max video duration constant */
  getMaxVideoDuration() {
    return MAX_VIDEO_DURATION;
  }

  /** Get storage tiers for display */
  getStorageTiers() {
    return { ...STORAGE_TIERS };
  }
}

// ── Singleton ──
let pinataInstance = null;

export const getPinataService = () => {
  if (!pinataInstance) {
    pinataInstance = new PinataService();
  }
  return pinataInstance;
};

export { STORAGE_TIERS, MAX_VIDEO_DURATION };
export default PinataService;
